import { zodResolver } from "@hookform/resolvers/zod";
import { changePassword, patchProfile } from "@stryker/core/api/accounts";
import { profileQueryOptions } from "@stryker/core/queries";
import { QueryKeys } from "@stryker/core/queryKeys";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import EyeOffIcon from "@/assets/icons/eye-off.svg?react";
import EyeIcon from "@/assets/icons/eye-open.svg?react";
import FileUploader from "@/components/FileUploader";
import PhoneInput from "@/components/PhoneInput";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { useToast } from "@/components/ui/use-toast";
import { hasSpecialSymbolRegex } from "@/constants/regex";

export default function Profile() {
  return (
    <div className="flex flex-col">
      <Typography variant="display-lg" weight="semibold" className="mb-2">
        User Profile
      </Typography>
      <ChangeProfileInfo />
      <ChangePassword />
    </div>
  );
}

const profileScheme = z.object({
  first_name: z.string(),
  last_name: z.string(),
  email: z.string().email({ message: "Email is required" }).min(1, "Email is required"),
});

type ProfileScheme = z.infer<typeof profileScheme>;
function ChangeProfileInfo() {
  const { data } = useQuery(profileQueryOptions());
  const [phoneNumber, setPhoneNumber] = useState(data?.phone_number);
  const queryClient = useQueryClient();
  const [avatar, setAvatar] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    setPhoneNumber(data?.phone_number);
  }, [data?.phone_number]);

  const { mutate, isPending } = useMutation({
    mutationFn: patchProfile,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.Profile] });
    },
    onError: (error: string) => {
      toast({ variant: "destructive", title: "Error", description: error?.toString() });
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ProfileScheme>({
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
    },
    values: data,
    resolver: zodResolver(profileScheme),
  });

  const onUpdateProfile = (data: any) => {
    if (avatar) {
      const formData = new FormData();
      phoneNumber && formData.append("phone_number", phoneNumber?.replace(/\s/g, ""));
      data.first_name && formData.append("first_name", data.first_name);
      data.last_name && formData.append("last_name", data.last_name);
      data.email && formData.append("email", data.email);
      formData.append("avatar", avatar);
      mutate({ data: formData, isMultipart: true });
    } else {
      mutate({ data: { ...data, phone_number: phoneNumber?.replace(/\s/g, "") } });
    }
  };

  return (
    <div className="mb-6">
      <Typography variant="text-lg" weight="semibold" className="mb-2 text-gray-900">
        Personal Information
      </Typography>
      <Typography variant="text-small" className="mb-4 border-b border-solid border-b-gray-200 pb-5 text-gray-600">
        Update your photo and personal details here.
      </Typography>
      <form
        onSubmit={handleSubmit(onUpdateProfile)}
        className="mb-2 flex flex-col rounded-xl border border-solid border-gray-200"
      >
        <div className="mx-6 mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 ">
          <TextInput
            label="First Name"
            placeholder="Enter your first name"
            error={errors.first_name?.message}
            {...register("first_name")}
          />
          <TextInput
            label="Last Name"
            placeholder="Enter your last name"
            error={errors.last_name?.message}
            {...register("last_name")}
          />
          <TextInput
            label="Email"
            placeholder="Enter your email"
            error={errors.email?.message}
            {...register("email")}
          />
          <PhoneInput value={phoneNumber || "+"} onChange={(phone: string) => setPhoneNumber(phone)} />
        </div>
        <div className="m-6">
          <FileUploader avatarUrl={data?.avatar} setFile={({ file }) => setAvatar(file)} />
        </div>
        <Button
          disabled={isPending}
          type="submit"
          className="w-initial mx-6 mb-4 mt-4 w-[326px] self-center md:self-end"
        >
          Save changes
        </Button>
      </form>
    </div>
  );
}

const passwordScheme = z.object({
  currentPassword: z.string(),
  newPassword: z
    .string()
    .min(1, { message: "Password is required" })
    .regex(hasSpecialSymbolRegex, "Must contain one special character"),
  confirmNewPassword: z.string().min(1, { message: "Confirm password is required" }),
});
type PasswordScheme = z.infer<typeof passwordScheme>;
function ChangePassword() {
  const { toast } = useToast();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordScheme>({
    resolver: zodResolver(passwordScheme),
  });

  const { mutate, isPending } = useMutation({
    mutationFn: changePassword,
    onError: (error: string) => {
      toast({ variant: "destructive", title: "Error", description: error?.toString() });
    },
  });

  const onChangePassword = (data: any) => {
    mutate({ old_password: data.currentPassword, password: data.newPassword, password2: data.confirmNewPassword });
  };

  return (
    <div>
      <Typography className="text-gray-900" variant="text-lg" weight="semibold">
        Password
      </Typography>
      <Typography className="mb-2 text-gray-600" variant="text-small">
        Please enter your current password to change your password.
      </Typography>
      <form onSubmit={handleSubmit(onChangePassword)} className="rounded-xl border border-solid border-gray-200">
        <div className="mx-6 mb-2 mt-6 flex flex-wrap gap-6 rounded border-b border-solid border-b-gray-200 pb-6">
          <TextInput
            className="w-[338px]"
            label="Current password"
            placeholder="Enter your current password"
            type={showCurrentPassword ? "text" : "password"}
            error={errors.currentPassword?.message}
            {...register("currentPassword")}
            endAdornment={
              showCurrentPassword ? (
                <EyeOffIcon className="cursor-pointer" onClick={() => setShowCurrentPassword(false)} />
              ) : (
                <EyeIcon className="cursor-pointer" onClick={() => setShowCurrentPassword(true)} />
              )
            }
          />
          <TextInput
            className="w-[338px]"
            label="New password"
            placeholder="Enter your new password"
            type={showNewPassword ? "text" : "password"}
            error={errors.newPassword?.message}
            {...register("newPassword")}
            endAdornment={
              showNewPassword ? (
                <EyeOffIcon className="cursor-pointer" onClick={() => setShowNewPassword(false)} />
              ) : (
                <EyeIcon className="cursor-pointer" onClick={() => setShowNewPassword(true)} />
              )
            }
          />
          <TextInput
            className="w-[338px]"
            label="Confirm password"
            placeholder="Confirm new password"
            type={showConfirmNewPassword ? "text" : "password"}
            error={errors.confirmNewPassword?.message}
            {...register("confirmNewPassword")}
            endAdornment={
              showConfirmNewPassword ? (
                <EyeOffIcon className="cursor-pointer" onClick={() => setShowConfirmNewPassword(false)} />
              ) : (
                <EyeIcon className="cursor-pointer" onClick={() => setShowConfirmNewPassword(true)} />
              )
            }
          />
        </div>
        <div className="self mb-4 mt-4 flex justify-between gap-4 px-6 md:justify-end">
          <Button variant="outline" onClick={() => reset()} className="flex-1 md:flex-none md:self-end">
            Cancel
          </Button>
          <Button disabled={isPending} type="submit" className="flex-1 md:flex-none md:self-end">
            Save changes
          </Button>
        </div>
      </form>
    </div>
  );
}
